/**
 * AW-0/AW-1 — AcDbAssocArray export-side dirty-diff + write-back wiring
 * (docs/MASTERPLAN.md AW-0/AW-1, `S-ARRAYASSOC-WRITE` follow-on subsystem).
 *
 * The by-construction dirty-diff CHOKE POINT (AW-0): projects each
 * associative array's DECODED import-time snapshot
 * (`entity.assocParamsSnapshot`, captured once by
 * `applyAssocArrayResolution`, dxfBridge.arrayAssocImport.ts) against the
 * entity's CURRENT §9.4 typed fields. A real structural comparison, not a
 * boolean flag a future write path could forget to set (CLAUDE.md
 * ПРИОРИТЕТ #1) — any future mutation of the 8 compared fields is caught BY
 * CONSTRUCTION, without the mutator having to know this gate exists.
 *
 * AW-1 (this module's `applyAssocArrayWriteBack`) is the mutation-aware
 * re-encoder AW-0 deferred: every DIRTY field is surgically patched into the
 * preserved graph via dxf-io's `patchAssocArrayGraphRawGroups`
 * (`assocArrayPatch.ts`) — patching VALUE STRINGS IN PLACE inside
 * `document.rawObjectGroups`, the SAME flat byte source AW-0 established as
 * the sole export channel (@codex round-1 P2 found that routing the graph
 * EXCLUSIVELY through a separate typed writer channel reordered the OBJECTS
 * section relative to the entity's own ext-dict DICTIONARY, which every real
 * associative array carries — patching in place, at existing positions,
 * cannot reorder anything). A field that could not be patched into EITHER
 * emission form (see `AssocArrayPatchSkipReason`) is surfaced via
 * `console.warn` instead of silently reverting with no signal — the
 * documented, planned boundary CLAUDE.md ПРИОРИТЕТ #1 allows for the
 * remaining, honestly-scoped gaps (item-list re-synthesis is AW-2; levels'
 * entity-conversion dead-slot wiring + expression read-only projection is
 * AW-3 — docs/MASTERPLAN.md AW.2).
 */
import type {
  AssocArrayFieldEdit,
  AssocArrayRectFieldName,
} from "@dxf-ai-editor/dxf-io";
import { patchAssocArrayGraphRawGroups } from "@dxf-ai-editor/dxf-io";

import type { ArrayRectEntity, DrawingDocument, DrawingEntity } from "./model";

/**
 * The 8 named §9.4 fields `applyAssocArrayResolution` decides at import time
 * and `assocParamsSnapshot` freezes — the AW-1 write-back matrix
 * (docs/MASTERPLAN.md AW.2). Shared by the snapshot capture (dxfBridge.
 * import.ts) and the dirty comparison below so the two can never drift.
 */
const ASSOC_ARRAY_DIRTY_DIFF_FIELDS = [
  "columnCount",
  "rowCount",
  "levelCount",
  "columnSpacing",
  "rowSpacing",
  "levelSpacing",
  "axisAngle",
  "rowElevation",
] as const;

/**
 * Walk every `Rectangular Associative Array` entity in the document — top-
 * level spaces only (snapshot-aware, mirrors `collectReferencedLinetypeNames`
 * in dxfBridge.export.ts). `blockChildEntitySchema` (model.ts) does not
 * currently admit `arrayRectEntitySchema` as a member — an associative array
 * cannot be a block-definition child in the typed model today (a pre-existing
 * schema gap, out of AW-0's scope), so there is nothing to walk there yet;
 * `applyAssocArrayResolution` itself only ever writes onto top-level space
 * entities for the same reason (dxfBridge.arrayAssocImport.ts).
 */
export function collectArrayRectEntities(
  document: DrawingDocument,
  entitiesSnapshot?: Map<string, DrawingEntity[]>,
): ArrayRectEntity[] {
  const out: ArrayRectEntity[] = [];
  for (const space of document.spaces) {
    const sourceEntities = entitiesSnapshot?.get(space.id) ?? space.entities;
    for (const entity of sourceEntities) {
      if (entity.type === "Rectangular Associative Array") out.push(entity);
    }
  }
  return out;
}

/**
 * True when the entity's CURRENT §9.4 fields diverge from the import-time
 * decoded snapshot on ANY of the 8 compared fields. A field the snapshot
 * never captured (the graph did not supply it — `applyAssocArrayResolution`
 * only writes a snapshot field when it actually resolved a value for it, see
 * dxfBridge.arrayAssocImport.ts) is never compared — absence is not a claim
 * (preserve-source-never-infer): only a snapshot-present field can be found
 * dirty.
 *
 * No entity-level `assocParamsSnapshot` at all (non-associative array, or an
 * associative array `applyAssocArrayResolution` never ran for) is NEVER
 * dirty — there is nothing decoded to diverge from.
 */
export function isAssocArrayDirty(entity: ArrayRectEntity): boolean {
  const snapshot = entity.assocParamsSnapshot;
  if (!snapshot) return false;
  for (const field of ASSOC_ARRAY_DIRTY_DIFF_FIELDS) {
    const snapshotValue = snapshot[field];
    if (snapshotValue === undefined) continue;
    if (entity[field] !== snapshotValue) return true;
  }
  return false;
}

/**
 * The by-construction dirty-diff choke point: every associative array whose
 * CURRENT typed fields diverged from what import decoded. Empty on the
 * (overwhelmingly common today) untouched-document path — the AW-0 acceptance
 * gate (byte-stable round-trip for untouched arrays) depends on this staying
 * empty whenever nothing mutated an array's typed fields after import.
 */
export function computeDirtyAssocArrayEntityIds(
  document: DrawingDocument,
  entitiesSnapshot?: Map<string, DrawingEntity[]>,
): string[] {
  const dirty: string[] = [];
  for (const entity of collectArrayRectEntities(document, entitiesSnapshot)) {
    if (isAssocArrayDirty(entity)) dirty.push(entity.id);
  }
  return dirty;
}

/**
 * AW-1 — project a dirty entity's per-field divergence into the
 * `AssocArrayFieldEdit[]` shape `patchAssocArrayGraphRawGroups` consumes.
 * ONLY the fields whose CURRENT value differs from the import-time snapshot
 * AND is still a NUMBER are included (mirrors `isAssocArrayDirty`'s
 * per-field comparison exactly, so the two can never disagree about which
 * fields changed) — an entity with no snapshot (non-associative, or a graph
 * `applyAssocArrayResolution` never touched) yields no edits.
 *
 * A field CLEARED to `undefined` (was graph-governed, the user removed it —
 * e.g. `entity.levelSpacing = undefined`) is dirty per `isAssocArrayDirty`
 * but is DELIBERATELY excluded here: this module only ever REPLACES an
 * EXISTING value string in place (`assocArrayPatch.ts`'s module doc) — it
 * has no "delete this named value param" / "renumber this positional
 * record" operation, which would be actual DXF structural surgery, not a
 * value patch. `computeAssocArrayClearedFields` below is the companion that
 * surfaces this residual gap instead of letting it silently keep the STALE
 * source value with zero signal (@codex P2).
 */
export function computeAssocArrayFieldEdits(
  entity: ArrayRectEntity,
): AssocArrayFieldEdit[] {
  const snapshot = entity.assocParamsSnapshot;
  if (!snapshot) return [];
  const edits: AssocArrayFieldEdit[] = [];
  for (const field of ASSOC_ARRAY_DIRTY_DIFF_FIELDS) {
    const snapshotValue = snapshot[field];
    if (snapshotValue === undefined) continue;
    const current = entity[field];
    if (current === undefined || current === snapshotValue) continue;
    // @codex P1 — a non-finite CURRENT value (NaN/Infinity) is excluded here
    // rather than packaged into an edit: `patchAssocArrayGraphRawGroups` →
    // `formatDxfNumber` (dxf-io/writer.ts) THROWS for a non-finite number
    // (`Cannot serialize non-finite DXF number`), uncaught by this module's
    // caller (`dxfBridge.export.ts`) — a single bad field would otherwise
    // abort the ENTIRE document export, not just this array/field, breaking
    // the graceful skip-with-warning degradation every OTHER failure mode in
    // this module already gets. See `computeAssocArrayNonFiniteFields`, the
    // companion that surfaces this as an explicit residual gap instead of a
    // silent drop (mirrors `computeAssocArrayClearedFields`).
    if (!Number.isFinite(current)) continue;
    edits.push({ field, value: current });
  }
  return edits;
}

/**
 * AW-1 (@codex P2 follow-up) — the companion half of
 * `computeAssocArrayFieldEdits`: every graph-governed field the entity
 * CLEARED to `undefined` since import (dirty per `isAssocArrayDirty`, but
 * NOT a `value: number` edit — see that function's doc for why this module
 * cannot patch a removal). Returned so the caller can surface it as an
 * explicit residual gap instead of silently keeping the stale exported
 * value with no signal.
 */
export function computeAssocArrayClearedFields(
  entity: ArrayRectEntity,
): AssocArrayRectFieldName[] {
  const snapshot = entity.assocParamsSnapshot;
  if (!snapshot) return [];
  const cleared: AssocArrayRectFieldName[] = [];
  for (const field of ASSOC_ARRAY_DIRTY_DIFF_FIELDS) {
    if (snapshot[field] === undefined) continue;
    if (entity[field] === undefined) cleared.push(field);
  }
  return cleared;
}

/**
 * AW-1 (@codex P1 follow-up) — the companion half of
 * `computeAssocArrayFieldEdits` for the non-finite-value exclusion
 * documented there: every graph-governed field the entity holds a NaN/
 * Infinity CURRENT value for (dirty per `isAssocArrayDirty` — NaN/Infinity
 * is never `=== snapshotValue` — but excluded from `edits` because it would
 * crash `formatDxfNumber` instead of patching). Returned so the caller can
 * surface it as an explicit residual gap instead of silently keeping the
 * stale exported value with no signal.
 */
export function computeAssocArrayNonFiniteFields(
  entity: ArrayRectEntity,
): AssocArrayRectFieldName[] {
  const snapshot = entity.assocParamsSnapshot;
  if (!snapshot) return [];
  const nonFinite: AssocArrayRectFieldName[] = [];
  for (const field of ASSOC_ARRAY_DIRTY_DIFF_FIELDS) {
    if (snapshot[field] === undefined) continue;
    const current = entity[field];
    if (current !== undefined && !Number.isFinite(current)) nonFinite.push(field);
  }
  return nonFinite;
}

/**
 * AW-1 write-back — the single call-site entry point for
 * `dxfBridge.export.ts`. Runs the AW-0 dirty-diff choke point, and for every
 * dirty array surgically patches its edited fields into `flatGroups` (the
 * document-wide verbatim OBJECTS stream — `document.rawObjectGroups`) via
 * dxf-io's `patchAssocArrayGraphRawGroups`. Pure — returns the (possibly
 * patched) groups array; `flatGroups` itself is never mutated.
 *
 * A field that could not be written into EITHER emission form (see
 * `AssocArrayPatchSkipReason` — expression-guarded, or genuinely no
 * persisted slot for it in either form) OR that was CLEARED to `undefined`
 * (@codex P2 — `computeAssocArrayClearedFields`, no removal operation
 * exists) is surfaced via `console.warn` with a `[AW-1]` marker so CI /
 * manual QA can catch a residual gap; a field written into at least one form
 * is silent (the common, expected case).
 */
export function applyAssocArrayWriteBack(
  document: DrawingDocument,
  flatGroups: readonly { code: number; value: string }[],
  entitiesSnapshot?: Map<string, DrawingEntity[]>,
): { code: number; value: string }[] {
  let groups = flatGroups.map((g) => ({ code: g.code, value: g.value }));
  const residualGaps: string[] = [];

  for (const entity of collectArrayRectEntities(document, entitiesSnapshot)) {
    if (!isAssocArrayDirty(entity)) continue;

    // @codex P2 — a field CLEARED to `undefined` is dirty but not a
    // patchable `value: number` edit (see `computeAssocArrayClearedFields`
    // doc). Surface it explicitly instead of letting `edits` end up empty
    // and the whole entity silently skipped with zero signal.
    for (const field of computeAssocArrayClearedFields(entity)) {
      residualGaps.push(`${entity.id}.${field} (cleared-to-undefined)`);
    }
    // @codex P1 — a NaN/Infinity field would crash the whole export if
    // packaged into an edit (see `computeAssocArrayFieldEdits`'s doc);
    // surface it as a residual gap instead, same as a cleared field.
    for (const field of computeAssocArrayNonFiniteFields(entity)) {
      residualGaps.push(`${entity.id}.${field} (non-finite-value)`);
    }

    // NOTE (removed-behavior audit, considered and NOT adopted here): a
    // count-affecting edit (columnCount/rowCount/levelCount) patches the
    // scalar "Items"/"Rows"/"Levels" param(s) but this module has NO
    // item-list re-synthesis (the persisted per-item AcDbAssocArrayItem
    // records + the action's re-evaluation status word are untouched —
    // explicitly deferred to AW-2, module doc above). A successfully-patched
    // count edit intentionally produces NO residual-gap warning (unlike a
    // field this module could not patch at all) — `dxfBridge.
    // arrayAssocWriteBack.test.ts`'s combinatorial matrix locks this in
    // across both emission forms for all 3 count fields. Surfacing a
    // per-edit warning here would be a scope-expanding UX decision this PR
    // does not make; the gap stays documented at the module level (AW-2,
    // docs/MASTERPLAN.md) rather than per-edit.
    const edits = computeAssocArrayFieldEdits(entity);
    if (edits.length === 0) continue;
    const result = patchAssocArrayGraphRawGroups(
      groups,
      {
        actionHandle: entity.assocHandles?.actionHandle,
        arrayParametersHandle: entity.assocHandles?.arrayParametersHandle,
        // @codex P2 ("Track the owning action per named field") — thread the
        // per-field split-ownership map through so a genuinely split array
        // (named params spread across more than one ACDBASSOCACTION) patches
        // each field into the record that actually owns it, instead of every
        // edit silently targeting (or being declined against) whichever
        // action `actionHandle` bound to. `undefined` for the common
        // single-action case (see `AssocArrayHandles.ownerHandleByField`'s
        // doc) — `patchAssocArrayGraphRawGroups` then falls back to
        // `actionHandle` uniformly, taking the pre-existing single-target path.
        ownerHandleByField: entity.assocHandles?.ownerHandleByField,
        // @codex P2 ("Preserve positional-axis units in hybrid arrays") —
        // thread an EXPLICIT boolean, never `undefined`: `dxf-io`'s patcher
        // treats `undefined` as "no caller opinion, keep converting" for
        // backward compat with callers that never resolved this flag at all,
        // but THIS caller always knows the answer (`entity
        // .axisAngleFromNamedForm` is only ever `true` or absent — see its
        // own doc, `dxfBridge.arrayAssocImport.ts`). Coercing absence to
        // `false` here is what actually disables the degrees→radians
        // conversion for a hybrid graph where the positional form won the
        // import-time merge (the model's CURRENT axisAngle is RADIANS, not
        // degrees, in that case).
        axisAngleFromNamedForm: entity.axisAngleFromNamedForm ?? false,
      },
      edits,
    );
    groups = result.groups;
    for (const outcome of result.outcomes) {
      // @codex P2 (finding "Warn when the authoritative form was skipped") —
      // a bare `namedFormApplied || positionalFormApplied` treats "at least
      // ONE form succeeded" as sufficient, but the two forms are NOT
      // symmetric on REIMPORT: `handleResolution.ts`'s
      // `mergeNamedRectValueParams` gives the POSITIONAL record precedence
      // for any field it supplies at all (`if (merged[field] !== undefined)
      // continue; // standalone record wins`) — the named form only fills a
      // field the positional form never had a slot for. So whichever value
      // reimport will ACTUALLY use is: the positional value when a
      // positional slot exists for this field (whether the patch to it
      // succeeded or was declined), and the named value ONLY when the
      // positional form never had this field at all
      // (`positionalFormSkipReason === "missing"`). A hybrid graph whose
      // positional slot EXISTS but was declined (`"expression-guarded"` /
      // `"unexpected-code"`) must warn even when the named form's OWN patch
      // succeeded — that named-form success is real (both emission forms
      // stay AutoCAD-consistent with each other) but it does NOT persist the
      // edit from the user's point of view, because the untouched, now-STALE
      // positional value is what a subsequent import will read back.
      const positionalSlotNeverExisted =
        outcome.positionalFormSkipReason === "missing";
      if (
        outcome.positionalFormApplied ||
        (positionalSlotNeverExisted && outcome.namedFormApplied)
      ) {
        continue;
      }
      // local Codex review (round-9, this PR) P3 — `"missing"` is both a
      // literal `AssocArrayPatchSkipReason` value AND the two forms' most
      // common outcome (the dominant real-AutoCAD shape carries only ONE of
      // the two forms), so a naive `??` chain that checks `namedFormSkipReason`
      // first would return `"missing"` for EVERY positional-only array whose
      // positional record WAS found but declined the edit for a specific
      // reason (`"expression-guarded"`/`"unexpected-code"`) — `"missing"` is
      // a non-nullish string, so `??` never falls through to the more
      // informative `positionalFormSkipReason`. Prefer whichever side has a
      // SPECIFIC (non-`"missing"`) reason — it reflects a form that actually
      // existed and was evaluated, which is always more actionable than "this
      // form doesn't exist at all" — before falling back to either `"missing"`.
      const specificReason =
        (outcome.namedFormSkipReason !== "missing" ? outcome.namedFormSkipReason : undefined) ??
        (outcome.positionalFormSkipReason !== "missing"
          ? outcome.positionalFormSkipReason
          : undefined);
      const reason =
        specificReason ?? outcome.namedFormSkipReason ?? outcome.positionalFormSkipReason ?? "missing";
      residualGaps.push(`${entity.id}.${outcome.field} (${reason})`);
    }
  }

  if (residualGaps.length > 0) {
    console.warn(
      `[AW-1] ${residualGaps.length} associative-array field edit(s) could ` +
        "not be written back to EITHER DXF emission form on this export " +
        `(docs/MASTERPLAN.md AW.2): ${residualGaps.join(", ")}`,
    );
  }

  return groups;
}
